import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !String(value).trim()) {
          params.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9odqdobdkpk99j7guv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SchemaOrg
        type="AboutPage"
        data={{
          name: '关于萝卜影视',
          description: '萝卜影视品牌介绍，了解我们收录电视剧全集与连续剧完整版的使命。',
          url: 'https://www.luobotv.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '萝卜影视',
            url: 'https://www.luobotv.com/',
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Page header */}
            <div className="mb-14 text-center max-w-2xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
                关于萝卜影视
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed">
                致力于为追剧爱好者提供最全的电视剧全集、连续剧完整版及电视剧大结局在线全集免费观看服务
              </p>
            </div>

            {/* Brand Story */}
            <section className="mb-16 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h2 className="font-heading text-xl text-foreground-900">品牌故事</h2>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  萝卜影视诞生于2024年，创始团队由一群资深追剧爱好者组成。在与众多剧迷交流的过程中，我们发现了一个普遍困扰：想看的电视剧分散在各个平台，找完整版费时费力，等更新更是磨人心态。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  于是，萝卜影视应运而生。我们致力于打造一站式电视剧在线全集免费观看平台，让每一位追剧爱好者都能在这里找到心仪的电视剧全集、连续剧完整版和电视剧大结局。无需辗转多个平台，无需苦苦等待更新——在萝卜影视，一次追完整部剧不是梦想。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们的团队每天都在不断扩充影视资源库，力求第一时间收录最新上线的电视剧全集与连续剧完整版，让用户在萝卜影视获得最快、最全、最好的电视剧在线全集免费观看体验。
                </p>
              </div>
            </section>

            {/* Mission & Values */}
            <section className="mb-16 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading text-xl text-foreground-900">使命与价值</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    icon: 'ri-heart-line',
                    title: '用户至上',
                    desc: '以追剧爱好者的需求为核心，持续优化用户体验，让每个用户都能轻松找到想看的电视剧全集。',
                  },
                  {
                    icon: 'ri-stack-line',
                    title: '资源丰富',
                    desc: '持续收录各类电视剧全集、连续剧完整版及电视剧大结局，涵盖古装剧、现代剧、悬疑剧等多种类型。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '品质保证',
                    desc: '严格筛选影视资源，确保高清画质和流畅播放，让用户享受优质的免费电视剧在线观看体验。',
                  },
                ].map((item) => (
                  <div key={item.title} className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className={`${item.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-secondary-500"></div>
                <h2 className="font-heading text-xl text-foreground-900">用户口碑</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    name: '追剧达人小王',
                    comment: '之前为了看一部电视剧的完整版跑了好几个平台，要么不全要么要会员。在萝卜影视一次就看完了电视剧全集和大结局，太方便了！',
                    rating: 5,
                  },
                  {
                    name: '剧荒少女',
                    comment: '萝卜影视的分类做得很好，按古装剧、现代剧、悬疑剧分得清清楚楚，找连续剧完整版非常方便，推荐给了身边所有追剧的朋友。',
                    rating: 5,
                  },
                  {
                    name: '深夜追剧人',
                    comment: '高清不缓冲，电视剧在线全集免费看，这在别的地方根本不敢想。萝卜影视的播放体验真的很好，已经成了我每天必打开的网站。',
                    rating: 5,
                  },
                  {
                    name: '历史剧爱好者老张',
                    comment: '作为一个历史剧迷，在萝卜影视找到了好多经典历史剧的完整版，很多都是别处找不到的资源。感谢萝卜影视让好剧不被埋没。',
                    rating: 4,
                  },
                ].map((review) => (
                  <div key={review.name} className="bg-background-100 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${
                            i < review.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      {review.comment}
                    </p>
                    <span className="text-xs text-foreground-400">—— {review.name}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="max-w-2xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h2 className="font-heading text-xl text-foreground-900">联系我们</h2>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <p className="text-sm text-foreground-500 mb-6">
                  如果您有任何关于电视剧全集、连续剧完整版的建议或问题，欢迎通过以下表单与我们联系。我们会在1-2个工作日内回复您。
                </p>

                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-check-line text-primary-500 text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-lg text-foreground-900 mb-1">提交成功！</h3>
                    <p className="text-sm text-foreground-500">感谢您的反馈，我们会尽快回复。</p>
                  </div>
                ) : (
                  <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                        {formError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-send-plane-line"></i>
                      提交留言
                    </button>
                  </form>
                )}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}